import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useInstitutionPositionTypes, useInstitutionAuthorityMutations } from "../hooks";
import { searchMinisters, searchInstitutionMembers } from "@/Services/institutions.service";
import { getAllDistricts, getAllRegions } from "@/Services/regions.service";
import type { InstitutionPositionType, InstitutionScope, AuthorityWithPerson } from "../types";
import type { MinisterSearchResult } from "@/Services/institutions.service";
import type { InstitutionMember } from "../types";

// ── Types ─────────────────────────────────────────────────────────────────────

type LocationRow = { id: string; name: string | null; code: string | null };
type RegionRow = LocationRow & { district_name: string | null };
type PersonTab = "minister" | "member";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  institutionId: string;
  /** Pre-set when clicking a specific national position card */
  positionType?: InstitutionPositionType;
  /** Pre-set scope — when undefined the user picks it */
  scope?: InstitutionScope;
  regionId?: string;
  districtId?: string;
  /** Current authority in this slot (for "Cambiar" flow) */
  currentAuthority?: AuthorityWithPerson;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PersonRow({
  name,
  subtitle,
  disabled,
  tag,
  onClick,
}: {
  name: string | null;
  subtitle: string | null;
  disabled?: boolean;
  tag?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <User className="size-5 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name ?? "—"}</p>
        {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {tag && <span className="text-xs text-muted-foreground">{tag}</span>}
    </button>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export function InstitutionAuthorityDialog({
  isOpen,
  onClose,
  institutionId,
  positionType: presetPosition,
  scope: presetScope,
  regionId: presetRegionId,
  districtId: presetDistrictId,
  currentAuthority,
}: Props) {
  const today = new Date().toISOString().slice(0, 10);

  // ── Form state ──
  const [scope, setScope] = useState<InstitutionScope>(presetScope ?? "national");
  const [regionId, setRegionId] = useState<string>(presetRegionId ?? "");
  const [districtId, setDistrictId] = useState<string>(presetDistrictId ?? "");
  const [positionTypeId, setPositionTypeId] = useState<string>(presetPosition?.id ?? "");
  const [startedAt, setStartedAt] = useState(today);
  const [personTab, setPersonTab] = useState<PersonTab>("minister");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Search results ──
  const [ministers, setMinisters] = useState<MinisterSearchResult[]>([]);
  const [members, setMembers] = useState<InstitutionMember[]>([]);
  const [searching, setSearching] = useState(false);

  // ── Location lists ──
  const [districts, setDistricts] = useState<LocationRow[]>([]);
  const [regions, setRegions] = useState<RegionRow[]>([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const { assign } = useInstitutionAuthorityMutations(institutionId);

  // Position types filtered by current scope
  const { data: positionTypes = [] } = useInstitutionPositionTypes(institutionId, scope);

  // Load districts + regions once on open
  useEffect(() => {
    if (!isOpen) return;
    getAllDistricts().then((d) =>
      setDistricts(
        d
          .map((x) => ({ id: x.id, name: x.name ?? null, code: x.code ?? null }))
          .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
      ),
    );
    getAllRegions().then((r) => {
      const mapped = r.map((x) => ({
        id: x.id,
        name: x.name ?? null,
        code: x.code ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        district_name: (x as any).district?.name ?? null,
      }));
      mapped.sort((a, b) => {
        const d = (a.district_name ?? "").localeCompare(b.district_name ?? "");
        return d !== 0 ? d : (a.name ?? "").localeCompare(b.name ?? "");
      });
      setRegions(mapped);
    });
  }, [isOpen]);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setScope(presetScope ?? "national");
    setRegionId(presetRegionId ?? "");
    setDistrictId(presetDistrictId ?? "");
    setPositionTypeId(presetPosition?.id ?? "");
    setStartedAt(today);
    setSearch("");
    setMinisters([]);
    setMembers([]);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced person search
  const runSearch = useCallback(
    (q: string) => {
      setSearching(true);
      const fn = personTab === "minister"
        ? searchMinisters(q)
        : searchInstitutionMembers(q);
      fn.then((res) => {
        if (personTab === "minister") setMinisters(res as MinisterSearchResult[]);
        else setMembers(res as InstitutionMember[]);
      }).finally(() => setSearching(false));
    },
    [personTab],
  );

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(search), 280);
    return () => clearTimeout(debounceRef.current);
  }, [search, personTab, isOpen, runSearch]);

  async function handleSelect(ministerId?: string, memberId?: string) {
    if (!positionTypeId) return;
    setSaving(true);
    try {
      await assign({
        positionTypeId,
        scope,
        regionId: scope === "region" ? regionId || null : null,
        districtId: scope === "district" ? districtId || null : null,
        ministerId: ministerId ?? null,
        institutionMemberId: memberId ?? null,
        startedAt,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const scopeLabels: Record<InstitutionScope, string> = {
    national: "Nacional",
    region: "Regional",
    district: "Distrital",
  };

  const locationMissing =
    (scope === "region" && !regionId) || (scope === "district" && !districtId);
  const positionMissing = !positionTypeId;
  const canSelect = !locationMissing && !positionMissing && !saving;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !saving && !o && onClose()}>
      <DialogContent className="flex max-h-[88vh] flex-col sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {currentAuthority ? "Cambiar autoridad" : "Asignar autoridad"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-1">
          {/* ── Scope selector (only when not pre-set) ── */}
          {!presetScope && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Ámbito</label>
              <div className="flex gap-2">
                {(["national", "region", "district"] as InstitutionScope[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => { setScope(s); setRegionId(""); setDistrictId(""); setPositionTypeId(""); }}
                    className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                      scope === s
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background hover:bg-accent"
                    }`}
                  >
                    {scopeLabels[s]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Region/District picker ── */}
          {scope === "region" && !presetRegionId && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Región</label>
              <select
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Seleccionar región…</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.district_name ? `${r.district_name} — ` : ""}{r.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {scope === "district" && !presetDistrictId && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Distrito</label>
              <select
                value={districtId}
                onChange={(e) => setDistrictId(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Seleccionar distrito…</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code ? `${d.code} — ` : ""}{d.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ── Position type picker (only when not pre-set) ── */}
          {!presetPosition && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Cargo</label>
              <select
                value={positionTypeId}
                onChange={(e) => setPositionTypeId(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Seleccionar cargo…</option>
                {positionTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* ── Started at ── */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Fecha de inicio</label>
            <input
              type="date"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              disabled={saving}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* ── Person tabs ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Persona</label>
            <div className="flex gap-1 rounded-md border border-border p-1">
              {(["minister", "member"] as PersonTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => { setPersonTab(tab); setSearch(""); }}
                  className={`flex-1 rounded px-3 py-1 text-xs font-medium transition-colors ${
                    personTab === tab
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent"
                  }`}
                >
                  {tab === "minister" ? "Ministro" : "Miembro"}
                </button>
              ))}
            </div>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o código…"
              disabled={saving || locationMissing || positionMissing}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            />

            <div className="max-h-52 overflow-y-auto rounded-md border border-border">
              {searching ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : personTab === "minister" ? (
                ministers.length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground">
                    {search ? "Sin resultados" : "Escribe para buscar"}
                  </p>
                ) : (
                  ministers.map((m) => (
                    <PersonRow
                      key={m.id}
                      name={m.full_name}
                      subtitle={m.adb_code}
                      disabled={!canSelect}
                      tag={m.id === currentAuthority?.minister_id ? "(actual)" : undefined}
                      onClick={() => handleSelect(m.id, undefined)}
                    />
                  ))
                )
              ) : (
                members.length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground">
                    {search ? "Sin resultados" : "Escribe para buscar"}
                  </p>
                ) : (
                  members.map((m) => (
                    <PersonRow
                      key={m.id}
                      name={m.full_name}
                      subtitle={m.dni ?? null}
                      disabled={!canSelect}
                      tag={m.id === currentAuthority?.institution_member_id ? "(actual)" : undefined}
                      onClick={() => handleSelect(undefined, m.id)}
                    />
                  ))
                )
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
